import React from "react";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import { siteConfig } from "../config/siteConfig";
import sunilImg from "../assets/sunil.jpg";
import {
  Users,
  Phone,
  MessageCircle,
  Mail,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";

const Partners = () => {
  const { t, isRTL } = useLanguage();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Partners",
        "item": "https://skwoolens.trivanahospitality.com/en/partners"
      }
    ]
  };

  const partnersSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": [
      {
        "@type": "Person",
        "name": "Nikhil Banthia",
        "jobTitle": "Partner",
        "worksFor": {
          "@type": "WholesaleStore",
          "name": "S.K. Woollens"
        },
        "telephone": "+91-7014581250",
        "email": "NIKHILBANTH783@GMAIL.COM"
      },
      {
        "@type": "Person",
        "name": "Sunil Banthia",
        "jobTitle": "Partner",
        "worksFor": {
          "@type": "WholesaleStore",
          "name": "S.K. Woollens"
        },
        "telephone": "+91-9829109294"
      }
    ]
  };

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="Leadership & Partners | Sunil Banthia & Nikhil Banthia | S.K. Woollens"
        description="Meet the leadership behind S.K. Woollens in Bikaner: Sunil Banthia (Domestic Procurement & Mandi Operations) and Nikhil Banthia (Client Partnerships & Sourcing)."
        keywords="Sunil Banthia S.K. Woollens, Nikhil Banthia S.K. Woollens, Bikaner wool partners, wool traders leadership Bikaner"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/partners"
        schema={[breadcrumbSchema, partnersSchema]}
      />
      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/20 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="max-w-3xl space-y-4">
            <div className="wool-badge">
              <Users className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Leadership</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
              {t("partners.title")}
            </h1>
            <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
              {t("partners.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* 2. Partner Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {siteConfig.partners.map((partner) => {
            const initials = partner.id === "nikhil-banthia" ? "NB" : "SB";
            const imageSrc = partner.id === "sunil-banthia" ? sunilImg : null;
            return (
              <div
                key={partner.id}
                className="bg-white border border-wool-border rounded-3xl p-8 shadow-subtle flex flex-col justify-between space-y-6 hover:border-wool-bronze/50 transition"
              >
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden bg-wool-sand/60 border border-wool-border shrink-0 flex items-center justify-center">
                      {imageSrc ? (
                        <img
                          src={imageSrc}
                          alt={partner.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : null}
                      <span className="text-xl font-bold text-wool-bronze">{initials}</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-wool-charcoal font-display">
                        {partner.name}
                      </h3>
                      <div className="text-xs font-semibold text-wool-bronze uppercase tracking-wider mt-0.5">
                        {partner.role}
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-wool-bg border border-wool-border/80 rounded-2xl text-xs text-wool-slate leading-relaxed">
                    <span className="font-semibold text-wool-charcoal block mb-1">
                      Key Responsibilities:
                    </span>
                    {partner.id === "sunil-banthia"
                      ? "Domestic wool procurement across Indian mandis, Bikaner market operations, raw lot inspection, and long-standing supplier relationships."
                      : "International wool sourcing strategy, B2B yarn manufacturer partnerships, commercial negotiations, and modern supply chain coordination."}
                  </div>
                </div>

                <div className="space-y-2 pt-4 border-t border-wool-border/60">
                  {partner.whatsapp && (
                    <a
                      href={partner.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 bg-emerald-50 hover:bg-emerald-100/80 border border-emerald-200 rounded-xl text-xs font-semibold text-emerald-800 transition"
                    >
                      <div className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-emerald-600" />
                        <span>WhatsApp: +91 {partner.phone}</span>
                      </div>
                      <ArrowRight className={`w-3.5 h-3.5 text-emerald-700 ${isRTL ? "rotate-180" : ""}`} />
                    </a>
                  )}

                  <a
                    href={`tel:+91${partner.phoneRaw}`}
                    className="flex items-center justify-between p-3 bg-wool-sand/30 hover:bg-wool-sand/60 border border-wool-border rounded-xl text-xs font-semibold text-wool-charcoal transition"
                  >
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-wool-bronze" />
                      <span>Call: +91 {partner.phone}</span>
                    </div>
                    <ArrowRight className={`w-3.5 h-3.5 text-wool-muted ${isRTL ? "rotate-180" : ""}`} />
                  </a>

                  <a
                    href={`mailto:${partner.email}`}
                    className="flex items-center justify-between p-3 bg-wool-sand/30 hover:bg-wool-sand/60 border border-wool-border rounded-xl text-xs font-semibold text-wool-charcoal transition"
                  >
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-wool-bronze" />
                      <span>Email: {partner.email}</span>
                    </div>
                    <ArrowRight className={`w-3.5 h-3.5 text-wool-muted ${isRTL ? "rotate-180" : ""}`} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 3. Business Ethos */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-wool-charcoal text-white rounded-3xl p-8 sm:p-12 text-center max-w-4xl mx-auto space-y-4">
          <div className="wool-badge bg-white/10 text-wool-sand border-white/20 inline-flex">
            <ShieldCheck className="w-3.5 h-3.5 text-wool-sand" />
            <span>Commercial Integrity</span>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold font-display">
            Direct Access to Leadership
          </h3>
          <p className="text-xs sm:text-sm text-wool-sand/80 leading-relaxed max-w-2xl mx-auto">
            At S.K. Woollens, every client relationship is overseen directly by the partners to ensure clear specification adherence, transparent pricing, and prompt delivery.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Partners;
